import {
  type CreateAdminProjectDto,
  type UpdateAdminProjectDto,
  type V2AdminProjectDto,
  type V2AdminProjectsPageDto,
  type V2AdminProjectsSummaryDto,
  v2AdminProjectsControllerCreate,
  v2AdminProjectsControllerDetail,
  v2AdminProjectsControllerList,
  v2AdminProjectsControllerLookup,
  v2AdminProjectsControllerRemove,
  v2AdminProjectsControllerSummary,
  v2AdminProjectsControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminProjectLookupSchema,
  V2AdminProjectSchema,
  V2AdminProjectsPageSchema,
  V2AdminProjectsSummarySchema
} from '@idc-forms/schemas/api-v2/admin-projects'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/** Entrada mínima del selector de proyectos (`prj_` + nombre + código). */
/**
 * `society` es el `soc_` dueño, y cascadea el selector de negociaciones igual que la empresa
 * cascadea las sociedades. `currency` viaja porque el formulario de detalles la deriva del
 * proyecto elegido. Los dos los devolvía la API y el mapper los tiraba.
 */
export type ProjectV2LookupOption = {
  id: string
  name: string
  code: string
  society: string
  currency: string | null
}

/**
 * Administración de proyectos contra la API v2 (ADR 0019/0020). Consume el SDK
 * generado con el Bearer de la sesión staff v2, pagina por cursor y usa el
 * contrato de consulta rico. Identifica todo por id público (`prj_`); la sociedad
 * dueña viaja por `soc_`, autoría y autorizadores por `usr_`, nunca como ObjectId.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer`
 * con bracket notation. El serializer por defecto del SDK usa `deepObject` de UN
 * solo nivel y explota en runtime ante cualquier filtro anidado del ADR 0020.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type ProjectsAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type ProjectsAdminV2Row = V2AdminProjectsPageDto['data'][number]

function toListQuery(query: ProjectsAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const projectsAdminV2Service = {
  /** Proyectos activos (`prj_` + nombre + código) para los selects que los cascadean. */
  lookup: async (accessToken: string): Promise<ProjectV2LookupOption[]> => {
    const result = await v2AdminProjectsControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminProjectLookupSchema, result.data).data.map((project) => ({
      id: project.id,
      name: project.name,
      code: project.code,
      society: project.society,
      currency: project.currency ?? null
    }))
  },

  list: async (
    accessToken: string,
    query: ProjectsAdminV2Query
  ): Promise<V2AdminProjectsPageDto> => {
    const result = await v2AdminProjectsControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminProjectsPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<ProjectsAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminProjectsSummaryDto> => {
    const result = await v2AdminProjectsControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminProjectsSummarySchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminProjectDto> => {
    const result = await v2AdminProjectsControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminProjectSchema, result.data)
  },

  create: async (accessToken: string, body: CreateAdminProjectDto): Promise<V2AdminProjectDto> => {
    const result = await v2AdminProjectsControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminProjectDto
  ): Promise<V2AdminProjectDto> => {
    const result = await v2AdminProjectsControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminProjectsControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
